/**
 * Campañas, anuncios y vínculos con unidades (E3): lectura de
 * `GET /getmetacampaigns` y de las respuestas de link / unlink, más los
 * cambios optimistas. Funciones puras. Ver docs/maps/meta-ads.md § Campañas.
 */
import { readMetaFase1Signal } from './metaFase1Support';
import { readMetaWriteBlock } from './metaWriteBlock';
import { readMetaBudget, readMetaLifetime } from './metaBudget';
import { readMetaAdCounters, readMetaDelivery, readMetaRecommendations } from './metaDelivery';
import { metaObjectiveLabel, readMetaAdsManager, readMetaAdsManagerColumns } from './metaAdsManager';
import { readMetaLinks, readMetaTotals } from './metaAdsMoney';
import { readMetaAdCreative } from './metaAdPreview';
import { isDeletedMetaCampaign } from './metaDeletedCampaigns';
import { hasMetaTerrioMark, stripMetaTerrioMark } from './metaFormat';

export { isDeletedMetaCampaign };

/** Máximo de registros por request de vínculo (API). */
export const META_MAX_LINK_RECORDS = 50;

/** `effective_status` de Meta → texto corto. Desconocido → «Otro estado en Meta». */
const EFFECTIVE_STATUS_LABELS = {
  ACTIVE: 'Activo',
  PAUSED: 'Pausado',
  CAMPAIGN_PAUSED: 'Campaña pausada',
  ADSET_PAUSED: 'Conjunto pausado',
  DELETED: 'Eliminado',
  ARCHIVED: 'Archivado',
  IN_PROCESS: 'Procesando',
  WITH_ISSUES: 'Con problemas',
  PENDING_REVIEW: 'En revisión',
  DISAPPROVED: 'Rechazado',
  PREAPPROVED: 'Aprobado (sin publicar)',
  PENDING_BILLING_INFO: 'Falta el medio de pago',
};

const KIND_LABELS = { user_lot: 'Unidad', cadastral_lot: 'Lote', cad_parcel: 'Lote' };
const ITEM_KINDS = Object.keys(KIND_LABELS);

const str = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);
const count = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
};
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Texto del estado de Meta (`effective_status`, o `status` si falta). */
export function metaEffectiveStatusLabel(effectiveStatus) {
  const key = String(effectiveStatus || '').trim().toUpperCase();
  if (!key) return null;
  return EFFECTIVE_STATUS_LABELS[key] || 'Otro estado en Meta';
}

/** Clave estable de un registro (`user_lot:<uuid>`). */
// Ids sin distinguir mayúsculas: la API devuelve los uuid en minúsculas (p. ej. en `RECORD_NOT_FOUND.record`).
export const metaRecordKey = (rec) => `${rec?.itemKind}:${String(rec?.id ?? '').toLowerCase()}`;

/**
 * Registro vinculado o sugerido → forma del Front (la misma del picker de Leads:
 * `{ itemKind, id, label }`). Sin tipo válido o id → `null`.
 */
export function normalizeMetaRecord(raw) {
  const itemKind = str(raw?.item_kind ?? raw?.itemKind);
  const id = str(raw?.id == null ? '' : String(raw.id));
  if (!ITEM_KINDS.includes(itemKind) || !id) return null;
  return {
    itemKind,
    id,
    projectId: str(raw?.project_id ?? raw?.projectId),
    label: str(raw?.label) || '',
    linkedVia: raw?.linked_via === 'campaign' || raw?.linkedVia === 'campaign' ? 'campaign' : 'ad',
    linkedAt: str(raw?.linked_at ?? raw?.linkedAt),
    linkedByUserId: str(raw?.linked_by_user_id ?? raw?.linkedByUserId) || readMetaLinkedBy(raw)?.userId || null,
    linkedBy: readMetaLinkedBy(raw),
  };
}

/**
 * Registro vinculado → ítem con la forma de `lead_items` para `useLeadItemOpener`
 * (misma navegación de MapPage que los intereses de un lead). `null` si no se puede
 * resolver: tipo desconocido, sin id o parcela CAD sin `project_id`.
 */
export function metaRecordOpenItem(rec) {
  if (!rec?.id) return null;
  const id = metaRecordKey(rec);
  if (rec.itemKind === 'user_lot') return { id, item_kind: 'user_lot', user_lot_id: rec.id, item_label: rec.label };
  if (rec.itemKind === 'cadastral_lot') return { id, item_kind: 'cadastral_lot', lot_id: rec.id, item_label: rec.label };
  if (rec.itemKind === 'cad_parcel' && rec.projectId) {
    return { id, item_kind: 'cad_parcel', project_id: rec.projectId, parcel_id: rec.id, item_label: rec.label };
  }
  return null;
}

const LINKED_BY_ROLES = ['owner', 'editor', 'former_member'];

/** `linked_by` (Ajuste 3) → `{ userId, role, isMe, email }`; puede faltar (null). */
export function readMetaLinkedBy(raw) {
  const lb = raw?.linked_by ?? raw?.linkedBy;
  if (!lb || typeof lb !== 'object') return null;
  return {
    userId: str(lb.user_id ?? lb.userId),
    role: LINKED_BY_ROLES.includes(lb.role) ? lb.role : null,
    isMe: lb.is_me === true || lb.isMe === true,
    email: str(lb.email),
  };
}

/**
 * «Vinculado por …» de un registro vinculado, sin mostrar nunca un id.
 * Con `linked_by` (Ajuste 3): `is_me` → «por vos»; `owner` → «por el dueño»;
 * `editor` → «por {email}» si mira el dueño (email solo le llega a él), si no
 * «por un editor»; `former_member` → «por {email} (ya no es miembro)» / «por un
 * ex miembro». Sin `linked_by`: se deduce de `linked_by_user_id` (vos / dueño /
 * un editor). Sin dato → ''.
 * `ownerUserId` null = espacio propio (el que mira es el dueño).
 *
 * @param {{ linkedBy?: object|null, linkedByUserId?: string|null }} rec
 * @param {{ viewerUserId?: string|null, ownerUserId?: string|null }} ctx
 */
export function describeMetaLinkedBy(rec, { viewerUserId = null, ownerUserId = null } = {}) {
  const viewerIsOwner = !ownerUserId;
  const lb = rec?.linkedBy || null;
  if (lb && (lb.isMe || lb.role)) {
    if (lb.isMe) return 'Vinculado por vos';
    if (lb.role === 'owner') return 'Vinculado por el dueño';
    if (lb.role === 'editor') return viewerIsOwner && lb.email ? `Vinculado por ${lb.email}` : 'Vinculado por un editor';
    return viewerIsOwner && lb.email ? `Vinculado por ${lb.email} (ya no es miembro)` : 'Vinculado por un ex miembro';
  }
  const by = rec?.linkedByUserId ? String(rec.linkedByUserId).toLowerCase() : '';
  if (!by) return '';
  const viewer = viewerUserId ? String(viewerUserId).toLowerCase() : '';
  const owner = ownerUserId ? String(ownerUserId).toLowerCase() : viewer;
  if (viewer && by === viewer) return 'Vinculado por vos';
  if (owner && by === owner) return 'Vinculado por el dueño';
  return viewer ? 'Vinculado por un editor' : '';
}

const readRecords = (list) => {
  const seen = new Set();
  return (Array.isArray(list) ? list : []).map(normalizeMetaRecord).filter((r) => {
    if (!r || seen.has(metaRecordKey(r))) return false;
    seen.add(metaRecordKey(r));
    return true;
  });
};

/** «Unidad: Dúplex 1» (o solo el tipo sin label). */
export function describeMetaRecord(rec) {
  const kind = KIND_LABELS[rec?.itemKind] || 'Registro';
  return rec?.label ? `${kind}: ${rec.label}` : kind;
}

/** Estado de vínculo de un anuncio según sus registros (mantiene `unlinked_new` si sigue sin vínculos). */
function adLinkStatusFor(records, previous) {
  if (records.length === 0) return previous === 'unlinked_new' ? 'unlinked_new' : 'unlinked';
  return records.length === 1 ? 'linked' : 'shared';
}

function withAdRecords(ad, records, { sharedLabel } = {}) {
  const linkStatus = adLinkStatusFor(records, ad.linkStatus);
  return {
    ...ad,
    linkedRecords: records,
    linkedCount: records.length,
    isShared: records.length > 1,
    linkStatus,
    sharedLabel: records.length > 1 ? (sharedLabel || `Anuncio compartido con ${records.length} unidades`) : null,
    // Ya vinculado: la sugerencia no aplica más.
    suggestedRecords: records.length ? [] : ad.suggestedRecords,
  };
}

/** Anuncio de `getmetacampaigns` con defaults seguros. */
export function normalizeMetaAd(raw, campaign = {}) {
  const { metaUrl, adAccountId } = readMetaLinks(raw);
  const id = str(raw?.ad_id == null ? '' : String(raw.ad_id));
  if (!id) return null;
  const records = readRecords(raw?.linked_records);
  const apiStatus = ['unlinked', 'linked', 'shared', 'unlinked_new'].includes(raw?.link_status) ? raw.link_status : null;
  const effectiveStatus = str(raw?.effective_status) || str(raw?.status);
  const delivery = readMetaDelivery(raw, metaEffectiveStatusLabel(effectiveStatus), effectiveStatus);
  return {
    id,
    name: str(stripMetaTerrioMark(raw?.ad_name)) || str(stripMetaTerrioMark(raw?.name)) || `Anuncio ${id}`,
    campaignId: str(raw?.campaign_id) || campaign.id || null,
    adAccountId: adAccountId || campaign.adAccountId || null,
    currency: str(raw?.currency)?.toUpperCase() || campaign.currency || null,
    metaUrl,
    effectiveStatus,
    // Ajuste 6: `delivery_label` manda; sin él, la etiqueta de `effective_status`.
    statusLabel: delivery.label,
    delivery,
    isActiveInMeta: String(effectiveStatus || '').toUpperCase() === 'ACTIVE',
    // Ajuste 9: `is_active` de la API (null si no viene: API vieja).
    isActive: typeof raw?.is_active === 'boolean' ? raw.is_active : null,
    recommendations: readMetaRecommendations(raw),
    creativeType: str(raw?.creative_type),
    // Ajuste 12: `thumbnailUrl` (solo https; vence), `thumbnailFetchedAt`, `creativeKind`, `isVideo`.
    ...readMetaAdCreative(raw),
    // Ajuste 10: columnas del Administrador de anuncios (o el `metrics` viejo).
    adsManager: readMetaAdsManager(raw),
    linkStatus: apiStatus || adLinkStatusFor(records),
    linkedRecords: records,
    linkedCount: records.length,
    isShared: raw?.is_shared === true || records.length > 1,
    sharedLabel: str(raw?.shared_label),
    suggestedRecords: readRecords(raw?.suggested_records),
    metrics: raw?.metrics && typeof raw.metrics === 'object' ? raw.metrics : null,
    write: readMetaWriteBlock(raw?.write),
  };
}

function withCampaignCounts(campaign) {
  const ads = campaign.ads;
  const linked = ads.filter((a) => a.linkedCount > 0).length;
  let linkStatus = 'unlinked';
  if (ads.length && linked === ads.length) linkStatus = 'linked';
  else if (linked > 0) linkStatus = 'partial';
  return {
    ...campaign,
    adsLinkedCount: linked,
    adsUnlinkedNewCount: ads.filter((a) => a.linkStatus === 'unlinked_new').length,
    linkStatus,
  };
}

/** Ajuste 9: entregas que cuentan como «activa» (los verdes de Meta). */
export const META_ACTIVE_DELIVERY_STATUSES = [
  'ACTIVE_DELIVERING', 'ACTIVE_NO_DELIVERY', 'SCHEDULED', 'IN_REVIEW', 'IN_PROCESS', 'WITH_ISSUES',
];

// «Activa» (ajuste 9): `is_active` de la API manda. API vieja: `delivery_status` en
// META_ACTIVE_DELIVERY_STATUSES (y, si viene, `active_ads_count` > 0); sin código,
// `delivering_ads_count` > 0; con solo `delivery_label`, no; sin campos de entrega,
// `effective_status === ACTIVE`. `is_delivering` nunca cuenta (es «tuvo impresiones hoy o ayer»).
function metaCampaignActive(raw, delivery, counters, isActiveInMeta) {
  if (typeof raw?.is_active === 'boolean') return raw.is_active;
  if (delivery.status) {
    const active = META_ACTIVE_DELIVERY_STATUSES.includes(delivery.status.toUpperCase());
    const activeAds = raw?.active_ads_count == null ? null : Number(raw.active_ads_count);
    return active && (activeAds == null || !Number.isFinite(activeAds) || activeAds > 0);
  }
  if (counters.deliveringAdsCount != null) return counters.deliveringAdsCount > 0;
  if (delivery.known) return false;
  return isActiveInMeta;
}

/** Campaña de `getmetacampaigns` con sus anuncios. */
export function normalizeMetaCampaign(raw) {
  const { metaUrl, adAccountId } = readMetaLinks(raw);
  const id = str(raw?.campaign_id == null ? '' : String(raw.campaign_id));
  if (!id) return null;
  const effectiveStatus = str(raw?.effective_status) || str(raw?.status);
  const delivery = readMetaDelivery(raw, metaEffectiveStatusLabel(effectiveStatus), effectiveStatus);
  const counters = readMetaAdCounters(raw);
  const isActiveInMeta = String(effectiveStatus || '').toUpperCase() === 'ACTIVE';
  const base = {
    id,
    name: str(stripMetaTerrioMark(raw?.name)) || `Campaña ${id}`,
    // Creada desde Terrio (marca « [TR…]» en el nombre crudo): su anuncio lleva al link público del registro.
    createdInTerrio: hasMetaTerrioMark(raw?.name),
    objective: str(raw?.objective),
    // Ajuste 10: «Interacción», «Clientes potenciales»… (`objective_label`; si no, `objective`).
    objectiveLabel: metaObjectiveLabel(raw),
    adsManager: readMetaAdsManager(raw),
    effectiveStatus,
    statusLabel: delivery.label,
    delivery,
    isActiveInMeta,
    // «Activa» para los contadores (ver `metaCampaignActive`; nunca `is_delivering`).
    isDeliveringNow: metaCampaignActive(raw, delivery, counters, isActiveInMeta),
    isActive: typeof raw?.is_active === 'boolean' ? raw.is_active : null,
    activeAdsCount: raw?.active_ads_count == null || !Number.isFinite(Number(raw.active_ads_count))
      ? null : Math.max(0, Math.trunc(Number(raw.active_ads_count))),
    ...counters,
    recommendations: readMetaRecommendations(raw),
    adAccountId,
    currency: str(raw?.currency)?.toUpperCase() || null,
    metaUrl,
    metrics: raw?.metrics && typeof raw.metrics === 'object' ? raw.metrics : null,
    // Fase 1: presupuesto y gasto de toda la vida (`null` si la API no los manda).
    budget: readMetaBudget(raw?.budget),
    lifetime: readMetaLifetime(raw?.lifetime),
    // F2a: acciones en Meta (null sin la bandera de escritura → sin acciones).
    write: readMetaWriteBlock(raw?.write),
  };
  const ads = (Array.isArray(raw?.ads) ? raw.ads : []).map((a) => normalizeMetaAd(a, base)).filter(Boolean);
  const campaign = withCampaignCounts({ ...base, ads, adsCount: count(raw?.ads_count) || ads.length });
  // Si la API manda los contadores, mandan (puede haber anuncios que no vienen en `ads`).
  if (['unlinked', 'partial', 'linked'].includes(raw?.link_status)) campaign.linkStatus = raw.link_status;
  if (raw?.ads_linked_count != null) campaign.adsLinkedCount = count(raw.ads_linked_count);
  if (raw?.ads_unlinked_new_count != null) campaign.adsUnlinkedNewCount = count(raw.ads_unlinked_new_count);
  return campaign;
}

/**
 * `data` de `GET /getmetacampaigns`.
 *
 * Ajuste 9: `activeCampaignsCount` / `totalCampaignsCount` (no dependen del filtro) y
 * `hiddenAdsCount` (anuncios de las campañas ocultas); `null` si la API no los manda.
 *
 * Eliminadas (`isDeletedMetaCampaign`, medida temporal): no entran en `campaigns` y
 * `totalCampaignsCount` no las cuenta (`total_campaigns_count` − eliminadas). Con la lista
 * completa («Todas», `includeInactive`) se cuentan acá; con «Activas» solo se sabe por
 * `knownDeleted` (`{ rawTotal, count }` de una carga anterior con el mismo
 * `total_campaigns_count`). Si no se sabe, `totalCampaignsCount` = `null` («Todas» sin número)
 * y `hasCampaignCounts` sigue en `true` (la API sí manda conteos).
 *
 * @returns {{ period: object|null, totals: object, campaigns: object[], hiddenCampaignsCount: number,
 *   activeCampaignsCount: number|null, totalCampaignsCount: number|null, hiddenAdsCount: number|null,
 *   hasCampaignCounts: boolean, rawTotalCampaignsCount: number|null, deletedCampaignIds: string[],
 *   deletedCountKnown: boolean }}
 */
export function readMetaCampaigns(res, { includeInactive = false, knownDeleted = null } = {}) {
  const data = res?.data && typeof res.data === 'object' ? res.data : {};
  const hidden = Number(data.hidden_campaigns_count);
  const optional = (value) => {
    const n = Number(value);
    return value == null || !Number.isFinite(n) || n < 0 ? null : Math.trunc(n);
  };
  const rawCampaigns = Array.isArray(data.campaigns) ? data.campaigns : [];
  const deletedCampaignIds = rawCampaigns.filter(isDeletedMetaCampaign)
    .map((raw) => str(raw?.campaign_id == null ? '' : String(raw.campaign_id))).filter(Boolean);
  const apiActive = optional(data.active_campaigns_count);
  const apiTotal = optional(data.total_campaigns_count);
  // Lista completa: «Todas», o todas activas (las eliminadas nunca son activas → 0).
  const listComplete = includeInactive || data.include_inactive === true
    || (apiActive != null && apiTotal != null && apiActive >= apiTotal);
  let deletedCount = null;
  if (listComplete) deletedCount = deletedCampaignIds.length;
  else if (knownDeleted && knownDeleted.rawTotal === apiTotal) deletedCount = knownDeleted.count;
  const totalCampaignsCount = apiTotal == null || deletedCount == null
    ? null
    : Math.max(apiActive || 0, apiTotal - deletedCount);
  const totals = readMetaTotals('campaigns', data);
  const byCurrencyRaw = Array.isArray(data.account_totals_by_currency) ? data.account_totals_by_currency : [];
  return {
    // Ajuste 10: catálogo de columnas, nota del alcance y bloque de los totales.
    adsManagerColumns: readMetaAdsManagerColumns(data),
    // Ajuste 11: frescura de los datos guardados (texto listo de la API; null = API vieja / sin cuentas).
    dataStale: data.data_stale === true,
    dataThrough: str(data.data_through),
    dataFreshnessText: str(data.data_freshness_text),
    lastSyncedAt: str(data.last_synced_at),
    // Fase 1: señal tentativa de que la API la soporta (`metaFase1Support.js`); null = API vieja.
    budgetAvailable: readMetaFase1Signal(data),
    adsManagerReachNote: str(data.ads_manager_reach_note),
    totalsAdsManager: data.account_totals && typeof data.account_totals === 'object'
      ? readMetaAdsManager(data.account_totals, { metrics: data.account_totals, stripMoney: totals.mixed })
      : null,
    totalsAdsManagerByCurrency: byCurrencyRaw
      .map((row) => ({
        currency: str(row?.currency)?.toUpperCase() || null,
        stats: readMetaAdsManager(row, { metrics: row?.metrics && typeof row.metrics === 'object' ? row.metrics : row }),
      }))
      .filter((row) => row.currency && row.stats),
    activeCampaignsCount: apiActive,
    totalCampaignsCount,
    hasCampaignCounts: apiActive != null && apiTotal != null,
    rawTotalCampaignsCount: apiTotal,
    deletedCampaignIds,
    deletedCountKnown: listComplete,
    hiddenAdsCount: optional(data.hidden_ads_count),
    period: data.period && data.period.since ? { since: data.period.since, until: data.period.until } : null,
    totals,
    // Ajuste 6: campañas que la API ocultó por inactivas (solo sin «Mostrar inactivas»).
    hiddenCampaignsCount: Number.isFinite(hidden) && hidden > 0 ? Math.trunc(hidden) : 0,
    // Eliminadas fuera (temporal): ni en la lista, ni en búsquedas, ni en Análisis / Presupuesto.
    campaigns: rawCampaigns.filter((raw) => !isDeletedMetaCampaign(raw)).map(normalizeMetaCampaign).filter(Boolean),
  };
}

/** Etiqueta del vínculo de un anuncio. */
export function metaAdLinkLabel(ad) {
  if (ad.linkStatus === 'unlinked_new') return 'Anuncio nuevo sin vincular';
  if (!ad.linkedCount) return 'Sin vincular';
  if (ad.linkedCount > 1) return ad.sharedLabel || `Anuncio compartido con ${ad.linkedCount} unidades`;
  return ad.linkedRecords[0]?.label || describeMetaRecord(ad.linkedRecords[0]);
}

/** Etiqueta del vínculo de una campaña. */
export function metaCampaignLinkLabel(campaign) {
  if (campaign.linkStatus === 'linked') return 'Vinculada';
  if (campaign.linkStatus === 'partial') {
    return `${campaign.adsLinkedCount} de ${plural(campaign.adsCount, 'anuncio vinculado', 'anuncios vinculados')}`;
  }
  return 'Sin vincular';
}

/** Unidades vinculadas a algún anuncio de la campaña (sin repetir), con cuántos anuncios cada una. */
export function campaignLinkedRecords(campaign) {
  const byKey = new Map();
  campaign.ads.forEach((ad) => ad.linkedRecords.forEach((rec) => {
    const key = metaRecordKey(rec);
    const prev = byKey.get(key);
    byKey.set(key, prev ? { ...prev, adsCount: prev.adsCount + 1 } : { ...rec, adsCount: 1 });
  }));
  return [...byKey.values()];
}

/**
 * Dónde se muestra la unidad (una sola vez, ajuste de acciones de vínculo):
 * - Si TODOS los anuncios (los visibles) tienen el mismo conjunto de unidades, no vacío,
 *   se muestra solo en la campaña (`campaignRecords`) y ningún anuncio la repite.
 * - Si no hay conjunto común, la campaña no muestra unidades y cada anuncio con
 *   unidades muestra las suyas (`adIdsWithUnits`).
 *
 * @param {{ ads: Array<{ id: string, linkedRecords: object[] }> }} campaign
 * @returns {{ hasCommon: boolean, campaignRecords: object[], adIdsWithUnits: string[] }}
 */
export function metaCampaignUnitsPlacement(campaign) {
  const ads = Array.isArray(campaign?.ads) ? campaign.ads : [];
  const setKey = (ad) => [...new Set((ad?.linkedRecords || []).map(metaRecordKey))].sort().join('|');
  const keys = ads.map(setKey);
  const hasCommon = keys.length > 0 && keys[0] !== '' && keys.every((k) => k === keys[0]);
  const commonKey = hasCommon ? keys[0] : null;
  return {
    hasCommon,
    campaignRecords: hasCommon ? campaignLinkedRecords(campaign) : [],
    adIdsWithUnits: ads.filter((ad, i) => keys[i] !== '' && keys[i] !== commonKey).map((ad) => ad.id),
  };
}

const mapCampaign = (campaigns, campaignId, fn) => campaigns.map((c) => (c.id === campaignId ? withCampaignCounts(fn(c)) : c));
const mapAd = (campaigns, adId, fn) => campaigns.map((c) => (c.ads.some((a) => a.id === adId)
  ? withCampaignCounts({ ...c, ads: c.ads.map((a) => (a.id === adId ? fn(a) : a)) })
  : c));

const mergeRecords = (current, added, linkedVia) => {
  const keys = new Set(current.map(metaRecordKey));
  const extra = added.map((r) => normalizeMetaRecord({ ...r, item_kind: r.itemKind ?? r.item_kind, linked_via: linkedVia }))
    .filter((r) => r && !keys.has(metaRecordKey(r)));
  return [...current, ...extra];
};
const removeRecords = (current, removed) => {
  if (removed == null) return [];
  const keys = new Set(removed.map((r) => metaRecordKey(normalizeMetaRecord(r) || r)));
  return current.filter((r) => !keys.has(metaRecordKey(r)));
};

/** Optimista: vincular un anuncio con registros. */
export const optimisticLinkAd = (campaigns, adId, records) => mapAd(campaigns, adId, (ad) => withAdRecords(ad, mergeRecords(ad.linkedRecords, records, 'ad')));

/** Optimista: desvincular un anuncio (`records` null = de todas). */
export const optimisticUnlinkAd = (campaigns, adId, records) => mapAd(campaigns, adId, (ad) => withAdRecords(ad, removeRecords(ad.linkedRecords, records)));

/** Optimista: vincular la campaña entera (cada anuncio de hoy). */
export const optimisticLinkCampaign = (campaigns, campaignId, records) => mapCampaign(campaigns, campaignId, (c) => ({
  ...c,
  ads: c.ads.map((ad) => withAdRecords(ad, mergeRecords(ad.linkedRecords, records, 'campaign'))),
}));

/** Optimista: desvincular la campaña (`records` null = todo). */
export const optimisticUnlinkCampaign = (campaigns, campaignId, records) => mapCampaign(campaigns, campaignId, (c) => ({
  ...c,
  ads: c.ads.map((ad) => withAdRecords(ad, removeRecords(ad.linkedRecords, records))),
}));

/** Aplica el `data` de linkadunit / unlinkadunit (lo que dice la API manda). */
export function applyAdLinkResponse(campaigns, data) {
  const adId = str(data?.ad_id == null ? '' : String(data.ad_id));
  if (!adId || !Array.isArray(data?.linked_records)) return campaigns;
  return mapAd(campaigns, adId, (ad) => withAdRecords(ad, readRecords(data.linked_records), { sharedLabel: str(data.shared_label) }));
}

/** Aplica el `data` de linkcampaignunit / unlinkcampaignunit (`data.ads[]`). */
export function applyCampaignLinkResponse(campaigns, data) {
  const list = Array.isArray(data?.ads) ? data.ads : [];
  return list.reduce((acc, item) => applyAdLinkResponse(acc, item), campaigns);
}

/**
 * Índice del registro con error de un `VALIDATION_ERROR` (`field: "records[3]"`), o `null`.
 */
export function readMetaRecordErrorIndex(res) {
  const m = /^records\[(\d+)\]$/.exec(String(res?.field || '').trim());
  return m ? Number(m[1]) : null;
}

/** Para el badge «N nuevos»: anuncios nuevos sin vincular en campañas ya vinculadas. */
export const countUnlinkedNewAds = (campaigns) => campaigns.reduce((n, c) => n + c.ads.filter((a) => a.linkStatus === 'unlinked_new').length, 0);

/** Campaña «activa» con la misma regla del toggle (ajuste 9: `is_active`; si no, `isDeliveringNow`). */
const campaignIsActive = (c) => (typeof c?.isActive === 'boolean' ? c.isActive : c?.isDeliveringNow === true);

/**
 * Ids de las campañas activas sin vincular del todo (sus anuncios activos tienen
 * alguno sin unidad = `linkStatus` `unlinked` o `partial`). No depende del toggle
 * Activas/Todas: las inactivas y los anuncios inactivos no cuentan (misma regla
 * que `ads.unlinked_count` de getmetaconnection, pero por campaña). Como
 * `linkedCount` se actualiza al vincular / desvincular, la cifra también.
 *
 * @param {object[]} campaigns De `normalizeMetaCampaigns`.
 * @returns {string[]}
 */
export function metaUnlinkedCampaignIds(campaigns) {
  return (campaigns || []).filter((c) => {
    if (!campaignIsActive(c)) return false;
    // Anuncio activo: `is_active` de la API; API vieja, `effective_status` ACTIVE.
    const ads = (c.ads || []).filter((a) => (typeof a.isActive === 'boolean' ? a.isActive : a.isActiveInMeta === true));
    if (ads.length === 0) return false;
    return ads.some((a) => !a.linkedCount);
  }).map((c) => c.id);
}

/**
 * Campañas para «Configuración de Meta»: conteos de la API (`active_campaigns_count` /
 * `total_campaigns_count`) y, si no vienen, la lista cargada. `null` mientras carga.
 *
 * @returns {{ total: number, active: number|null, inactive: number|null }|null}
 */
export function metaCampaignMix(state, { includeInactive = false } = {}) {
  if (!state?.loaded || state.loading || state.error) return null;
  const apiActive = state.activeCampaignsCount;
  const apiTotal = state.totalCampaignsCount;
  if (apiActive != null && apiTotal != null && apiTotal >= apiActive) {
    return { total: apiTotal, active: apiActive, inactive: apiTotal - apiActive };
  }
  const campaigns = state.campaigns || [];
  const active = campaigns.filter(campaignIsActive).length;
  // Con conteos pero sin saber cuántas eliminadas hay («Activas»): solo las activas.
  if (apiActive != null && state.hasCampaignCounts && !includeInactive) return { total: null, active: apiActive, inactive: null };
  // Sin conteos y con solo las activas cargadas no sabemos cuántas inactivas hay.
  if (!includeInactive) return { total: null, active, inactive: null };
  return { total: campaigns.length, active, inactive: campaigns.length - active };
}
